import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { PureBiData, ClickDownloadButtonBiEvent } from '@wix/da-bi/pkg/events';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import { SubscriptionPurchaseFTUEContext } from '../../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import SubscribeToUnlockTooltip from '../../_partials/SubscribeToUnlockTooltip';
import ActionBarIcon from '../../_partials/ActionBarIcon';
import ActionBarButton from '../../_partials/items/ActionBarButton';
import { useVerification } from '../../../VerificationPopup/withVerification';
import DownloadButtonProps from './types';
import { getDeviationFreeDownload } from './permissions';
import DownloadLimitModal from './DownloadLimitModal';

const logger = ddt.createLogger('downloadbutton');

const FreeDownloadButton: React.FC<
  React.PropsWithChildren<DownloadButtonProps>
> = ({ deviation, className, onDownload, onMouseEnter, onMouseLeave }) => {
  const { t } = useTranslation();
  const { isLoggedIn, restrictToLoggedIn } = useRestrictToLoggedIn();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();
  const [showLimitModal, setShowLimitModal] = useState(false);

  // when the user first purchases a subscription, open the tooltip and highlight the icon
  const isButtonHighlighted =
    useContext(SubscriptionPurchaseFTUEContext) === 'tooltips';

  logger.log('FreeDownloadButton, deviation:', deviation);
  const { download, permission } = getDeviationFreeDownload(deviation);
  logger.log({ download, permission });
  if (!download) {
    return null;
  }

  const downloadRestrictions = deviation.extended?.downloadRestrictions;
  const isDownloadLimitReached =
    downloadRestrictions !== undefined &&
    downloadRestrictions.remaining <= 0;

  const wrapperProps = {
    className: className,
    forceVisibleTooltip: isButtonHighlighted,
  };

  const buttonProps = {
    biData: PureBiData<ClickDownloadButtonBiEvent>({
      evid: 350,
      ispaiddownload: 0,
      deviationid: deviation.deviationId,
      sectionname: 'action_bar',
    }),
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  };

  // send anon to sisu for both free and premium downloads
  if (!isLoggedIn) {
    const tooltip = t('actionbar.download.mustLogIn');
    return (
      <ActionBarItemWrapper {...wrapperProps} tooltip={tooltip}>
        <ActionBarButton
          {...buttonProps}
          aria-label={tooltip}
          onClick={restrictToLoggedIn(ReferralType.ANON_DOWNLOAD)}
        >
          <ActionBarIcon isActive={isButtonHighlighted} icon={DownloadIcon} />
        </ActionBarButton>
      </ActionBarItemWrapper>
    );
  }

  // throw popup for unverified
  if (isVerificationNeeded) {
    const tooltip = t('actionbar.download.mustVerify');
    return (
      <ActionBarItemWrapper {...wrapperProps} tooltip={tooltip}>
        <ActionBarButton
          {...buttonProps}
          aria-label={tooltip}
          onClick={() => pushVerificationPopup('freedownload')}
        >
          <ActionBarIcon isActive={isButtonHighlighted} icon={DownloadIcon} />
        </ActionBarButton>
      </ActionBarItemWrapper>
    );
  }

  // in-tier, un-subbed free downloads are blocked with a special popup
  const subPreventsDownload =
    permission === 'needs_subscription' ||
    permission === 'blocked_by_subscription';
  if (subPreventsDownload) {
    logger.log('free download blocked by tier', deviation.tier);
    return (
      <SubscribeToUnlockTooltip
        className={className}
        deviation={deviation}
        forceVisible={isButtonHighlighted}
      >
        <ActionBarButton
          {...buttonProps}
          disabled
          aria-label={t('actionbar.freeDownload')}
        >
          <ActionBarIcon isActive={isButtonHighlighted} icon={DownloadIcon} />
        </ActionBarButton>
      </SubscribeToUnlockTooltip>
    );
  }

  // here, we know the user is logged in and the special case of
  // free downloads in subscription content is handled, so let them download
  const tooltip = t('actionbar.freeDownload');
  return (
    <ActionBarItemWrapper {...wrapperProps} tooltip={tooltip}>
      <ActionBarButton
        {...buttonProps}
        href={isDownloadLimitReached ? undefined : download.url}
        target={isDownloadLimitReached ? undefined : '_blank'}
        download={!isDownloadLimitReached}
        aria-label={tooltip}
        onClick={
          isDownloadLimitReached
            ? () => setShowLimitModal(true)
            : download.url
              ? (onDownload as () => void)
              : undefined
        }
      >
        <ActionBarIcon isActive={isButtonHighlighted} icon={DownloadIcon} />
      </ActionBarButton>
      {showLimitModal && downloadRestrictions && (
        <DownloadLimitModal
          allocation={downloadRestrictions.allocation}
          onRequestClose={() => setShowLimitModal(false)}
        />
      )}
    </ActionBarItemWrapper>
  );
};

FreeDownloadButton.displayName = 'FreeDownloadButton';
export default FreeDownloadButton;
